/**
 * AdSlot — universal image-based ad renderer for any placement key.
 *
 * Fetches `/api/ads/{location}` (existing backend). Auto-hides for users
 * with active paid subscriptions (backend returns `[]` for them). Tracks
 * clicks against `/api/ads/{campaign_id}/click`.
 *
 * Props:
 *   location: string — one of the AdLocation enum values
 *   variant:  "sidebar" | "banner" | "inline" | "footer"  (default "sidebar")
 *   className: extra classes
 *   dense:    boolean — compact renderer for tight spaces
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { ExternalLink, X } from 'lucide-react';

const DISMISS_PREFIX = 'ad_dismissed_';

const AdSlot = ({ location, variant = 'sidebar', className = '', dense = false }) => {
  const { API } = useContext(AuthContext);
  const [ads, setAds] = useState([]);
  const [index, setIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!location) return;
    // Client-side per-tab dismiss so users can hide a slot for the session.
    try {
      if (sessionStorage.getItem(DISMISS_PREFIX + location)) {
        setDismissed(true);
        return;
      }
    } catch (_) { /* noop */ }
    let cancelled = false;
    axios.get(`${API}/ads/${location}`)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setAds(list);
        setIndex(0);
      })
      .catch(() => { /* silent — ads are non-essential */ });
    return () => { cancelled = true; };
  }, [location, API]);

  if (dismissed || ads.length === 0) return null;
  const ad = ads[index % ads.length];
  if (!ad) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_PREFIX + location, '1'); } catch (_) { /* noop */ }
    setDismissed(true);
  };

  const goToUrl = () => {
    if (!ad.click_url) return;
    // Fire-and-forget click log
    axios.post(`${API}/ads/${ad.campaign_id}/click`).catch(() => { /* silent */ });
    window.open(ad.click_url, '_blank', 'noopener,noreferrer');
  };

  const image = ad.file_url;
  const alt   = ad.alt_text || ad.custom_title || 'Advertisement';
  const title = ad.custom_title;
  const desc  = ad.custom_description;
  const cta   = ad.custom_cta_text || 'Learn more';

  // ---- Renderers ----
  if (variant === 'banner') {
    return (
      <div className={`relative rounded-2xl overflow-hidden border border-border shadow-sm bg-card ${className}`} data-testid={`adslot-${location}`}>
        <button type="button" onClick={dismiss} className="absolute top-2 right-2 z-10 h-6 w-6 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center" aria-label="Dismiss ad">
          <X className="w-3 h-3" />
        </button>
        <button type="button" onClick={goToUrl} disabled={!ad.click_url} className="block w-full text-left group">
          {image && <img src={image} alt={alt} className="w-full h-32 md:h-44 object-cover group-hover:scale-[1.02] transition-transform" />}
          {(title || desc) && (
            <div className="p-4">
              {title && <div className="font-heading text-lg font-extrabold text-foreground">{title}</div>}
              {desc  && <div className="text-sm text-muted-foreground mt-1">{desc}</div>}
              {ad.click_url && (
                <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary group-hover:underline">
                  {cta} <ExternalLink className="w-3 h-3" />
                </div>
              )}
            </div>
          )}
        </button>
        <div className="absolute bottom-1 left-2 text-[9px] uppercase tracking-widest font-bold text-white/70 bg-black/40 rounded px-1">Ad</div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`relative rounded-xl border border-dashed border-border bg-muted/40 p-3 flex items-center gap-3 ${className}`} data-testid={`adslot-${location}`}>
        <span className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground bg-muted rounded px-1">Ad</span>
        {image && (
          <button type="button" onClick={goToUrl} disabled={!ad.click_url} className="flex-shrink-0">
            <img src={image} alt={alt} className="h-14 w-14 rounded-lg object-cover" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          {title && <div className="font-heading text-sm font-extrabold text-foreground truncate">{title}</div>}
          {desc  && <div className="text-xs text-muted-foreground line-clamp-2">{desc}</div>}
          {ad.click_url && (
            <button type="button" onClick={goToUrl} className="mt-1 text-[11px] font-bold text-primary hover:underline inline-flex items-center gap-0.5">
              {cta} <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
        <button type="button" onClick={dismiss} className="h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground" aria-label="Dismiss ad">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div className={`relative rounded-xl overflow-hidden border border-border bg-pro-soft ${className}`} data-testid={`adslot-${location}`}>
        <button type="button" onClick={dismiss} className="absolute top-2 right-2 z-10 h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground" aria-label="Dismiss ad">
          <X className="w-3 h-3" />
        </button>
        <button type="button" onClick={goToUrl} disabled={!ad.click_url} className="w-full flex items-center gap-3 p-3 text-left">
          {image && <img src={image} alt={alt} className="h-16 w-16 rounded-lg object-cover flex-shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="text-[9px] uppercase tracking-widest font-bold text-primary">Sponsored</div>
            {title && <div className="font-heading text-base font-extrabold text-foreground">{title}</div>}
            {desc  && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{desc}</div>}
          </div>
          {ad.click_url && (
            <span className="text-xs font-bold text-primary inline-flex items-center gap-1 flex-shrink-0">
              {cta} <ExternalLink className="w-3 h-3" />
            </span>
          )}
        </button>
      </div>
    );
  }

  // Compact overlay pill — designed to float in the corner of a video frame
  // or hero. Never blocks the underlying UI; dismissible.
  if (variant === 'overlay-pill') {
    return (
      <div className={`inline-flex items-center gap-2 rounded-full bg-black/70 backdrop-blur-md text-white shadow-lg pl-1 pr-2 py-1 max-w-[280px] ${className}`} data-testid={`adslot-${location}`}>
        <button type="button" onClick={goToUrl} disabled={!ad.click_url} className="flex items-center gap-2 group min-w-0">
          {image
            ? <img src={image} alt={alt} className="h-6 w-6 rounded-full object-cover flex-shrink-0" />
            : <span className="h-6 w-6 rounded-full bg-white/15 flex items-center justify-center text-[9px] font-bold flex-shrink-0">AD</span>}
          <span className="text-[10px] uppercase tracking-widest font-bold text-white/70 flex-shrink-0">Sponsor</span>
          {title && <span className="text-xs font-semibold truncate">{title}</span>}
        </button>
        <button type="button" onClick={dismiss} className="h-5 w-5 rounded-full hover:bg-white/15 flex items-center justify-center text-white/70 flex-shrink-0" aria-label="Dismiss ad">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // Slim horizontal bottom-banner — designed to slot under a video call or
  // hero and stay out of the way but ever-present.
  if (variant === 'bottom-banner') {
    return (
      <div className={`relative rounded-xl overflow-hidden border border-border bg-card shadow-sm ${className}`} data-testid={`adslot-${location}`}>
        <button type="button" onClick={goToUrl} disabled={!ad.click_url} className="w-full flex items-center gap-3 p-2.5 pr-10 text-left group">
          {image && <img src={image} alt={alt} className="h-10 w-10 md:h-12 md:w-12 rounded-lg object-cover flex-shrink-0" />}
          <div className="min-w-0 flex-1 flex items-center gap-3">
            <span className="text-[9px] uppercase tracking-widest font-bold text-primary flex-shrink-0">Sponsor</span>
            <div className="min-w-0 flex-1">
              {title && <div className="font-heading text-sm font-extrabold text-foreground truncate">{title}</div>}
              {desc  && <div className="text-xs text-muted-foreground truncate hidden md:block">{desc}</div>}
            </div>
          </div>
          {ad.click_url && (
            <span className="text-xs font-bold text-primary inline-flex items-center gap-1 flex-shrink-0 hidden sm:inline-flex">
              {cta} <ExternalLink className="w-3 h-3" />
            </span>
          )}
        </button>
        <button type="button" onClick={dismiss} className="absolute top-1/2 -translate-y-1/2 right-2 h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground" aria-label="Dismiss ad">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // Default: sidebar (portrait card)
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-border bg-card shadow-sm ${dense ? '' : 'mb-4'} ${className}`} data-testid={`adslot-${location}`}>
      <button type="button" onClick={dismiss} className="absolute top-2 right-2 z-10 h-6 w-6 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center" aria-label="Dismiss ad">
        <X className="w-3 h-3" />
      </button>
      <button type="button" onClick={goToUrl} disabled={!ad.click_url} className="block w-full text-left group">
        {image && <img src={image} alt={alt} className="w-full aspect-video object-cover group-hover:opacity-90 transition-opacity" />}
        <div className="p-3">
          <div className="text-[9px] uppercase tracking-widest font-bold text-primary">Sponsored</div>
          {title && <div className="font-heading text-sm font-extrabold text-foreground mt-1">{title}</div>}
          {desc  && <div className="text-xs text-muted-foreground mt-1 line-clamp-3">{desc}</div>}
          {ad.click_url && (
            <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary group-hover:underline">
              {cta} <ExternalLink className="w-3 h-3" />
            </div>
          )}
        </div>
      </button>
    </div>
  );
};

export default AdSlot;
